import axios from 'axios'
import bcrypt from 'bcryptjs'

interface User {
  id: string
  username: string
  isAdmin?: boolean
  storageUsed: number
  storageLimit: number
  themeColors?: ThemeColors | null
}

interface ThemeColors {
  secondary: {
    h: number
    s: number
    l: number
  }
}

// Mock users database
let mockUsers = [
  {
    id: 'admin-uuid',
    username: 'admin',
    password: 'valid$!1', // Store plain password for demo only
    isAdmin: true,
    storageUsed: 1.5 * 1024 * 1024 * 1024, // 1.5 GB
    storageLimit: 10 * 1024 * 1024 * 1024,  // 10 GB
    themeColors: null
  },
  {
    id: 'user-uuid',
    username: 'user',
    password: 'valid$!2', // Store plain password for demo only
    isAdmin: false,
    storageUsed: 0.5 * 1024 * 1024 * 1024, // 0.5 GB
    storageLimit: 5 * 1024 * 1024 * 1024,   // 5 GB
    themeColors: null
  }
]

// In a real application, this would be an API call to your backend
export const loginUser = async (username: string, password: string): Promise<User | null> => {
  // Find user in our mock database
  const foundUser = mockUsers.find(u => u.username === username && u.password === password)
  
  if (foundUser) {
    const user = {
      id: foundUser.id,
      username: foundUser.username,
      isAdmin: foundUser.isAdmin,
      storageUsed: foundUser.storageUsed,
      storageLimit: foundUser.storageLimit,
      themeColors: foundUser.themeColors
    }
    
    // Store user in localStorage for session persistence
    localStorage.setItem('user', JSON.stringify(user))
    
    return user
  }
  
  return null
}

export const checkSession = async (): Promise<User | null> => {
  const userJson = localStorage.getItem('user')
  if (!userJson) return null
  
  try {
    const user = JSON.parse(userJson)
    return user
  } catch (error) {
    console.error('Failed to parse user from localStorage:', error)
    return null
  }
}

export const logoutUser = async (): Promise<void> => {
  localStorage.removeItem('user')
}

export const updateUsername = async (userId: string, newUsername: string): Promise<User | null> => {
  try {
    // Check if username already exists (except for the current user)
    if (mockUsers.some(u => u.username === newUsername && u.id !== userId)) {
      throw new Error('Username already exists')
    }
    
    // Find and update the user in our mock database
    const userIndex = mockUsers.findIndex(u => u.id === userId)
    if (userIndex === -1) {
      throw new Error('User not found')
    }
    
    // Update the username
    mockUsers[userIndex].username = newUsername
    
    // Update localStorage if this is the current user
    const userJson = localStorage.getItem('user')
    if (userJson) {
      const currentUser = JSON.parse(userJson)
      if (currentUser.id === userId) {
        currentUser.username = newUsername
        localStorage.setItem('user', JSON.stringify(currentUser))
      }
    }
    
    // Return the updated user
    return {
      id: mockUsers[userIndex].id,
      username: mockUsers[userIndex].username,
      isAdmin: mockUsers[userIndex].isAdmin,
      storageUsed: mockUsers[userIndex].storageUsed,
      storageLimit: mockUsers[userIndex].storageLimit,
      themeColors: mockUsers[userIndex].themeColors
    }
  } catch (error) {
    console.error('Failed to update username:', error)
    throw error
  }
}

export const updatePassword = async (
  userId: string, 
  currentPassword: string, 
  newPassword: string
): Promise<boolean> => {
  try {
    // Find the user in our mock database
    const userIndex = mockUsers.findIndex(u => u.id === userId)
    if (userIndex === -1) {
      throw new Error('User not found')
    }
    
    // Verify current password
    if (mockUsers[userIndex].password !== currentPassword) {
      return false
    }
    
    // Update the password
    mockUsers[userIndex].password = newPassword
    
    return true
  } catch (error) {
    console.error('Failed to update password:', error)
    throw error
  }
}

export const updateThemeColors = async (
  userId: string,
  colors: ThemeColors | null
): Promise<boolean> => {
  try {
    const userIndex = mockUsers.findIndex(u => u.id === userId)
    if (userIndex === -1) {
      throw new Error('User not found')
    }
    
    mockUsers[userIndex].themeColors = colors
    
    // Update localStorage if this is the current user
    const userJson = localStorage.getItem('user')
    if (userJson) {
      const currentUser = JSON.parse(userJson)
      if (currentUser.id === userId) {
        currentUser.themeColors = colors
        localStorage.setItem('user', JSON.stringify(currentUser))
      }
    }
    
    return true
  } catch (error) {
    console.error('Failed to update theme colors:', error)
    return false
  }
}

export const getStorageInfo = async (userId: string): Promise<{ used: number, limit: number } | null> => {
  // In a real app, this would be an API call
  const userJson = localStorage.getItem('user')
  if (!userJson) return null
  
  try {
    const user = JSON.parse(userJson)
    return {
      used: user.storageUsed || 0,
      limit: user.storageLimit || 0
    }
  } catch (error) {
    console.error('Failed to get storage info:', error)
    return null
  }
}

// Admin functions for user management
export const getAllUsers = async (): Promise<User[]> => {
  // In a real app, this would be an API call
  // For demo purposes, we'll return our mock users without passwords
  return mockUsers.map(user => ({
    id: user.id,
    username: user.username,
    isAdmin: user.isAdmin,
    storageUsed: user.storageUsed,
    storageLimit: user.storageLimit,
    themeColors: user.themeColors
  }))
}

export const createUser = async (
  username: string, 
  password: string, 
  isAdmin: boolean,
  storageLimit: number
): Promise<User | null> => {
  try {
    // Check if username already exists
    if (mockUsers.some(u => u.username === username)) {
      throw new Error('Username already exists')
    }
    
    // Create new user
    const newUser = {
      id: `user-${Date.now()}`,
      username,
      password,
      isAdmin,
      storageUsed: 0,
      storageLimit: storageLimit * 1024 * 1024 * 1024, // Convert GB to bytes
      themeColors: null
    }
    
    // Add to our mock database
    mockUsers.push(newUser)
    
    // Return user without password
    return {
      id: newUser.id,
      username: newUser.username,
      isAdmin: newUser.isAdmin,
      storageUsed: newUser.storageUsed,
      storageLimit: newUser.storageLimit,
      themeColors: newUser.themeColors
    }
  } catch (error) {
    console.error('Failed to create user:', error)
    throw error
  }
}

export const updateUser = async (
  userId: string,
  updates: {
    username?: string
    isAdmin?: boolean
    storageLimit?: number
  }
): Promise<User | null> => {
  try {
    // Find user in our mock database
    const userIndex = mockUsers.findIndex(u => u.id === userId)
    
    if (userIndex === -1) {
      throw new Error('User not found')
    }
    
    // Check if new username already exists (if updating username)
    if (updates.username && 
        updates.username !== mockUsers[userIndex].username && 
        mockUsers.some(u => u.username === updates.username)) {
      throw new Error('Username already exists')
    }
    
    // Update user
    if (updates.username) mockUsers[userIndex].username = updates.username
    if (updates.isAdmin !== undefined) mockUsers[userIndex].isAdmin = updates.isAdmin
    if (updates.storageLimit !== undefined) {
      mockUsers[userIndex].storageLimit = updates.storageLimit * 1024 * 1024 * 1024 // Convert GB to bytes
    }
    
    // If updating the currently logged in user, update localStorage
    const userJson = localStorage.getItem('user')
    if (userJson) {
      const currentUser = JSON.parse(userJson)
      if (currentUser.id === userId) {
        if (updates.username) currentUser.username = updates.username
        if (updates.isAdmin !== undefined) currentUser.isAdmin = updates.isAdmin
        if (updates.storageLimit !== undefined) {
          currentUser.storageLimit = updates.storageLimit * 1024 * 1024 * 1024
        }
        localStorage.setItem('user', JSON.stringify(currentUser))
      }
    }
    
    // Return updated user without password
    return {
      id: mockUsers[userIndex].id,
      username: mockUsers[userIndex].username,
      isAdmin: mockUsers[userIndex].isAdmin,
      storageUsed: mockUsers[userIndex].storageUsed,
      storageLimit: mockUsers[userIndex].storageLimit,
      themeColors: mockUsers[userIndex].themeColors
    }
  } catch (error) {
    console.error('Failed to update user:', error)
    throw error
  }
}

export const deleteUser = async (userId: string): Promise<boolean> => {
  try {
    // Find user in our mock database
    const userIndex = mockUsers.findIndex(u => u.id === userId)
    
    if (userIndex === -1) {
      throw new Error('User not found')
    }
    
    // Don't allow deleting the admin user
    if (mockUsers[userIndex].username === 'admin') {
      throw new Error('Cannot delete admin user')
    }
    
    // Remove user
    mockUsers = mockUsers.filter(u => u.id !== userId)
    
    return true
  } catch (error) {
    console.error('Failed to delete user:', error)
    throw error
  }
}

export const resetUserPassword = async (userId: string, newPassword: string): Promise<boolean> => {
  try {
    // Find user in our mock database
    const userIndex = mockUsers.findIndex(u => u.id === userId)
    
    if (userIndex === -1) {
      throw new Error('User not found')
    }
    
    // Update password
    mockUsers[userIndex].password = newPassword
    
    return true
  } catch (error) {
    console.error('Failed to reset password:', error)
    throw error
  }
}